'use client';
import { useState } from 'react';

const queries = [
  {
    id: 'ui-task',
    label: 'Build a UI task that displays the top 5 alerting endpoints',
    category: 'Automation',
    response: {
      type: 'text',
      content: `Created a UI task named **"Top 5 Alerting Endpoints"** that fetches alerts, computes alert counts per endpoint, and returns the top 5 endpoints.\n\nWaiting for your approval to save it.`,
      action: 'Approve Action',
    },
  },
  {
    id: 'report',
    label: 'Create a formatted report on our current organization data — top open alerts, affected endpoints, and ownership',
    category: 'Reporting',
    response: {
      type: 'report',
      title: 'Security Operations Report — Open Alerts / Affected Endpoints / Ownership',
      generated: '2026-07-07',
      sections: [
        {
          heading: '1) Top open alerts',
          note: 'Sample view from current tenant data — alerts endpoint capped at 50 rows.',
          rows: [
            { alert: 'Potential modification of installed components to maintain persistence', severity: 'Medium', endpoint: 'desktop-l41glml' },
            { alert: 'Anomalous image loaded by a user process', severity: 'Medium', endpoint: 'desktop-l41glml' },
            { alert: 'Anomalous Windows script executing PowerShell', severity: 'High', endpoint: 'cylerian_ai_assistant_container, dnstest, desktop-65rv3h8' },
            { alert: 'Windows EDR brute forcing across multiple hosts from external IP 207.254.160.62', severity: 'High', endpoint: '169.254.45.29' },
          ],
        },
        {
          heading: '2) Affected endpoints',
          rows2: [
            { endpoint: 'desktop-l41glml', evidence: 'Multiple open alert records (persistence via installed components; anomalous image loaded)', owner: 'Unassigned' },
            { endpoint: 'dnstest', evidence: 'Ticket "anomalous windows script executing powershell"', owner: 'Unassigned' },
            { endpoint: 'desktop-65rv3h8', evidence: 'Ticket "anomalous windows script executing powershell"', owner: 'Unassigned' },
            { endpoint: '169.254.45.29', evidence: 'Ticket "Windows EDR brute forcing..."', owner: 'Unassigned' },
          ],
        },
        {
          heading: '3) Ownership',
          text: 'All open incidents returned status = unassigned. No assignee or owner fields were populated in the current alert or ticket payload. If you want this report to cover a specific time window or rank by occurrence count, let me know and I\'ll run aggregations.',
        },
      ],
    },
  },
  {
    id: 'accounts',
    label: 'Find accounts that were created in the last 7 days',
    category: 'Investigation',
    response: {
      type: 'accounts',
      content: `Queried your identity data for accounts created between **2026-06-30** and **2026-07-07**.\n\nFound **3 new accounts** across Microsoft 365 and Google Workspace:\n\n- **j.martinez@cylerian.com** — Microsoft 365, created Jul 2 (role: Analyst)\n- **svc-endpoint-monitor** — Service account, created Jul 3 (no MFA configured)\n- **t.nguyen@cylerian.com** — Google Workspace, created Jul 5 (role: Viewer)\n\nThe service account **svc-endpoint-monitor** has no MFA configured — do you want me to open a ticket to remediate?`,
    },
  },
  {
    id: 'lateral',
    label: 'Are there any alerts related to lateral movement or privilege escalation?',
    category: 'Threat Hunting',
    response: {
      type: 'text',
      content: `Yes — found **4 open alerts** matching lateral movement and privilege escalation patterns:\n\n- **Anomalous Windows script executing PowerShell** (High) — 3 endpoints affected: cylerian_ai_assistant_container, dnstest, desktop-65rv3h8. Active tickets open for 16–19 days.\n- **Windows EDR brute forcing across multiple hosts** (High) — source IP 207.254.160.62, targeting 169.254.45.29. Likely credential stuffing attempt.\n\nNeither set of alerts has an assigned owner. Would you like me to correlate these with authentication logs to determine if any accounts were successfully compromised?`,
    },
  },
];

const categoryColors = {
  'Automation': '#3495DB',
  'Reporting': '#8B5CF6',
  'Investigation': '#10B981',
  'Threat Hunting': '#F59E0B',
};

export default function CyraDemo() {
  const [active, setActive] = useState(null);

  const current = queries.find(q => q.id === active);

  return (
    <section className='cyra-demo'>
      <div className='cyra-demo__inner'>
        <div className='cyra-demo__header'>
          <p className='cyra-demo__eyebrow'>See Cyra in action</p>
          <h2 className='cyra-demo__title'>Ask anything. Get answers that act.</h2>
          <p className='cyra-demo__sub'>Click a query to see how Cyra responds.</p>
        </div>

        <div className='cyra-demo__layout'>
          <div className='cyra-demo__queries'>
            {queries.map(q => (
              <button
                key={q.id}
                className={`cyra-demo__query${active === q.id ? ' cyra-demo__query--active' : ''}`}
                aria-pressed={active === q.id}
                onClick={() => setActive(active === q.id ? null : q.id)}
                style={{ '--cat-color': categoryColors[q.category] }}
              >
                <span className='cyra-demo__query-cat'>{q.category}</span>
                <span className='cyra-demo__query-text'>{q.label}</span>
                <svg className='cyra-demo__query-arrow' width='16' height='16' viewBox='0 0 24 24' fill='none'>
                  <path d='M9 18l6-6-6-6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </button>
            ))}
          </div>

          <div className={`cyra-demo__response${current ? ' cyra-demo__response--visible' : ''}`} aria-live='polite' aria-atomic='true'>
            {!current && (
              <div className='cyra-demo__placeholder'>
                <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                  <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' stroke='#3495DB' strokeWidth='1.2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <p>Select a query to see Cyra&rsquo;s response</p>
              </div>
            )}

            {current && (
              <div className='cyra-demo__card'>
                <div className='cyra-demo__card-header'>
                  <div className='cyra-demo__card-icon'>
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                      <path d='M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z' stroke='#3495DB' strokeWidth='1.5' />
                      <path d='M12 8v4l3 3' stroke='#3495DB' strokeWidth='1.5' strokeLinecap='round' />
                    </svg>
                  </div>
                  <span className='cyra-demo__card-label'>Cyra</span>
                </div>

                <div className='cyra-demo__card-query'>
                  <p>{current.label}</p>
                </div>

                <div className='cyra-demo__card-body'>
                  {current.response.type === 'text' && (
                    <div className='cyra-demo__text'>
                      {current.response.content.split('\n').map((line, i) => {
                        if (!line.trim()) return null;
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <p key={i}>
                            {parts.map((part, j) =>
                              part.startsWith('**') && part.endsWith('**')
                                ? <strong key={j}>{part.slice(2, -2)}</strong>
                                : part
                            )}
                          </p>
                        );
                      })}
                      {current.response.action && (
                        <div className='cyra-demo__action-btn' role='status' aria-label={`Action: ${current.response.action}`}>
                          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                            <path d='M20 6L9 17l-5-5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                          {current.response.action}
                        </div>
                      )}
                    </div>
                  )}

                  {current.response.type === 'accounts' && (
                    <div className='cyra-demo__text'>
                      {current.response.content.split('\n').map((line, i) => {
                        if (!line.trim()) return null;
                        if (line.startsWith('- ')) {
                          const parts = line.slice(2).split(/(\*\*[^*]+\*\*)/g);
                          return (
                            <div key={i} className='cyra-demo__list-item'>
                              <span className='cyra-demo__bullet' />
                              <p>{parts.map((part, j) =>
                                part.startsWith('**') && part.endsWith('**')
                                  ? <strong key={j}>{part.slice(2, -2)}</strong>
                                  : part
                              )}</p>
                            </div>
                          );
                        }
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <p key={i}>
                            {parts.map((part, j) =>
                              part.startsWith('**') && part.endsWith('**')
                                ? <strong key={j}>{part.slice(2, -2)}</strong>
                                : part
                            )}
                          </p>
                        );
                      })}
                    </div>
                  )}

                  {current.response.type === 'report' && (
                    <div className='cyra-demo__report'>
                      <p className='cyra-demo__report-title'>{current.response.title}</p>
                      <p className='cyra-demo__report-date'>Generated: {current.response.generated}</p>
                      {current.response.sections.map((s, i) => (
                        <div key={i} className='cyra-demo__report-section'>
                          <h4>{s.heading}</h4>
                          {s.note && <p className='cyra-demo__report-note'>{s.note}</p>}
                          {s.rows && (
                            <div className='cyra-demo__table-wrap'>
                              <table className='cyra-demo__table'>
                                <thead>
                                  <tr><th>Alert</th><th>Severity</th><th>Endpoint</th></tr>
                                </thead>
                                <tbody>
                                  {s.rows.map((r, j) => (
                                    <tr key={j}>
                                      <td>{r.alert}</td>
                                      <td><span className={`cyra-demo__sev cyra-demo__sev--${r.severity.toLowerCase()}`}>{r.severity}</span></td>
                                      <td><code>{r.endpoint}</code></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {s.rows2 && (
                            <div className='cyra-demo__table-wrap'>
                              <table className='cyra-demo__table'>
                                <thead>
                                  <tr><th>Endpoint</th><th>Evidence</th><th>Owner</th></tr>
                                </thead>
                                <tbody>
                                  {s.rows2.map((r, j) => (
                                    <tr key={j}>
                                      <td><code>{r.endpoint}</code></td>
                                      <td>{r.evidence}</td>
                                      <td><span className='cyra-demo__unassigned'>{r.owner}</span></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {s.text && <p className='cyra-demo__report-text'>{s.text}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
